import React, { type KeyboardEventHandler, useEffect } from 'react'
import type { Entry } from 'contentful'
import type {
  TypeComponentQuoteSkeleton,
  TypeComponentTableSkeleton,
  TypeResourceImageSkeleton,
  TypeSectionSetOfCardsSkeleton,
} from '@/types/ctf'
type QuoteEntry = Entry<TypeComponentQuoteSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ImageEntry = Entry<TypeResourceImageSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type SetOfCards = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type TableEntry = Entry<TypeComponentTableSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

import {
  type ValueAnimationTransition,
  type PanInfo,
  animate,
  useIsomorphicLayoutEffect,
  useMotionValue,
  useMotionValueEvent,
} from 'framer-motion'
import { useEventListener } from 'usehooks-ts'

import usePrevious from '@/utils/usePrevious'
import useDebouncedCallback from '@/utils/useDebouncedCallback'

import { getCircularIndex } from './utils'
import { cx } from '@/utils/strings'
import { isPrint } from '@/utils/print'

import VirtualizedSlide from './virtualized-slide-track'

import Close from '@/icons/bwi/close.svg'
import Download from '@/icons/bwi/download.svg'
import Desktop from '@/icons/bwi/desktop-alt.svg'

import Image from '@/components/ui/image'
import Quote from '@/components/ui/quote'
import PresentationSlide from '@/components/ui/slider/presentation-slide'

export type SliderProps = {
  slides?: any[]
  customCss?: any
  pdfPath?: string
}

const DEFAULT_RENDER_COUNT = 2
const isPrintView = isPrint()

const dragTransition: ValueAnimationTransition = {
  type: 'spring',
  bounce: 0,
}
const navigateTransition: ValueAnimationTransition = {
  type: 'spring',
  bounce: 0,
  stiffness: 5000,
  damping: 10,
}

/**
 * Infinite slider component
 */
export default function Slider({ slides, customCss, pdfPath = '' }: SliderProps) {
  const items = slides?.map((slide, index) => {
    if (!slide) return null
    const { fields } = slide
    if (slide.sys.contentType.sys.id === 'componentQuote') {
      const quote = fields as QuoteEntry['fields']
      return (
        <div key={index} className="mx-4 md:mx-14 mb-14">
          <Quote {...quote} />
        </div>
      )
    }
    if (slide.sys.contentType.sys.id === 'resourceImage') {
      const image = fields as ImageEntry['fields']
      return (
        <div
          key={index}
          className={cx(
            'mx-14 mb-14 max-w-full h-full flex items-center justify-center',
            !image.zoomable && 'pointer-events-none'
          )}
        >
          <Image {...image} />
        </div>
      )
    }

    if (slide.sys.contentType.sys.id === 'sectionSetOfCards') {
      const sectionCards = fields as SetOfCards['fields']

      return (
        <div key={index}>
          <PresentationSlide {...sectionCards} />
        </div>
      )
    }

    if (slide.sys.contentType.sys.id === 'componentTable') {
      const tableData = fields as TableEntry['fields']

      return (
        <div key={index}>
          <PresentationSlide table={tableData} />
        </div>
      )
    }

    return null
  })

  const x = useMotionValue(0)
  const parentRef = React.useRef<HTMLDivElement>(null)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [index, setIndex] = React.useState(0)
  const previousIndex = usePrevious(index)
  const activeIndex = getCircularIndex(index, items?.length || 0)

  const [renderCount, setRenderCount] = React.useState({
    forward: DEFAULT_RENDER_COUNT,
    behind: DEFAULT_RENDER_COUNT,
  })

  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const calculateNewX = (i: number) => -i * (containerRef.current?.clientWidth || 0)

  const handleNavigateLeft = () => {
    animate(x, calculateNewX(index - 1), navigateTransition)
    setIndex(index - 1)
  }
  const handleNavigateRight = () => {
    animate(x, calculateNewX(index + 1), navigateTransition)
    setIndex(index + 1)
  }
  const handleKeyDown: KeyboardEventHandler = React.useCallback(
    (event) => {
      if (event.key === 'ArrowRight') {
        parentRef.current?.focus()
        handleNavigateRight()
      } else if (event.key === 'ArrowLeft') {
        parentRef.current?.focus()
        handleNavigateLeft()
      }
    },
    [index, setIndex]
  )

  const handleEndDrag = (e: Event, dragProps: PanInfo) => {
    if (!dragProps) return
    const clientWidth = containerRef.current?.clientWidth || 0

    const { offset, velocity } = dragProps

    if (Math.abs(velocity.y) > Math.abs(velocity.x)) {
      animate(x, calculateNewX(index), dragTransition)
      return
    }

    if (offset.x > clientWidth / 4) {
      setIndex(index - 1)
    } else if (offset.x < -clientWidth / 4) {
      setIndex(index + 1)
    } else {
      animate(x, calculateNewX(index), dragTransition)
    }
  }

  useEffect(() => {
    const delta = index - (previousIndex || 0)
    if (delta > DEFAULT_RENDER_COUNT) {
      setRenderCount({ forward: delta, behind: DEFAULT_RENDER_COUNT })
    } else if (delta < -DEFAULT_RENDER_COUNT) {
      setRenderCount({ forward: DEFAULT_RENDER_COUNT, behind: Math.abs(delta) })
    }
    const controls = animate(x, calculateNewX(index), dragTransition)

    return () => {
      controls.stop
    }
  }, [index])

  const handleResize = useDebouncedCallback(() => {
    // Render correct x position after resize
    x.set(calculateNewX(index))
  }, 50)

  useIsomorphicLayoutEffect(() => {
    handleResize()
  }, [])
  useEventListener('resize', handleResize)

  useMotionValueEvent(x, 'animationStart', () => {
    if (parentRef.current) {
      parentRef.current.ariaBusy = 'true'
    }
  })

  useMotionValueEvent(x, 'animationComplete', () => {
    if (parentRef.current) {
      parentRef.current.ariaBusy = 'false'
    }

    // Reset render count to default after animation is complete
    if (renderCount.forward !== DEFAULT_RENDER_COUNT || renderCount.behind !== DEFAULT_RENDER_COUNT) {
      setRenderCount({
        forward: DEFAULT_RENDER_COUNT,
        behind: DEFAULT_RENDER_COUNT,
      })
    }
  })

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
    handleResize()
  }

  const NavBtnStyles =
    'flex items-center justify-center w-9 h-9 text-[16px] rounded-full bg-white shadow-md hover:bg-secondary-50 hover:active:scale-95 will-change-transform transition-[opacity, transform] duration-100 select-none border-secondary-100 border-1 '
  const NavBtnIconStyle = 'font-bwi text-primaryBlue w-[16px] h-[16px] leading-none -translate-x-[0.0625rem]'
  const NavButton = ({ children, dir }: React.ComponentProps<'button'>) => {
    return (
      <button
        type="button"
        aria-label={dir === 'Left' ? 'Navigate left' : 'Navigate right'}
        className={cx(NavBtnStyles)}
        onClick={dir === 'Left' ? handleNavigateLeft : handleNavigateRight}
        data-testid={dir === 'Left' ? 'slider-nav-left' : 'slider-nav-right'}
      >
        {children}
      </button>
    )
  }

  const renderSliderNavButtons = () => (
    <>
      <NavButton dir="Left">
        <span className={NavBtnIconStyle}>&#xe96b;</span>
      </NavButton>

      <div className={cx(!isFullscreen && 'text-indigoBlue', isFullscreen && 'text-white', 'text-sm w-14 text-center')}>
        {activeIndex + 1} / {items?.length || 0}
      </div>

      <NavButton>
        <span className={cx(NavBtnIconStyle, 'translate-x-0.5')}>&#xe91b;</span>
      </NavButton>
    </>
  )

  const SliderWrapper = React.useCallback(
    ({ children }: { children: React.ReactNode }) => {
      return isFullscreen ? (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
          <div className="w-full h-full flex flex-col justify-center">
            <div className="p-10">{children}</div>
            <button
              onClick={toggleFullscreen}
              className="absolute top-4 right-4 bg-black bg-opacity-70 text-white p-2 rounded-full hover:bg-opacity-90 transition-all z-10"
              aria-label="Close fullscreen"
            >
              <Close className="text-white text-4xl w-6" />
            </button>
          </div>
        </div>
      ) : (
        <>{children}</>
      )
    },
    [isFullscreen]
  )

  return !isPrintView ? (
    <SliderWrapper>
      <div
        role="tablist"
        className={cx(
          'outline-hidden w-full rounded-4xl overflow-hidden [--bw-slider-nav-button-distance:0.5rem] border shadow-even-around-small',
          customCss
        )}
        ref={parentRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        data-testid="slider"
      >
        <div className="relative w-full">
          <VirtualizedSlide
            x={x}
            index={index}
            totalCount={items?.length || 0}
            onDragEnd={handleEndDrag}
            containerRef={containerRef}
            forwardRenderCount={renderCount.forward}
            behindRenderCount={renderCount.behind}
          >
            {({ index: currentIndex }) => {
              const itemIndex = getCircularIndex(currentIndex, items?.length || 0)
              return <>{items?.[itemIndex]}</>
            }}
          </VirtualizedSlide>
        </div>
      </div>

      <div className="flex flex-row py-4 space-x-3 items-center px-6">
        <div className="flex-grow">
          {(items?.length || 0) > 1 && <div className=" flex flex-row items-center ">{renderSliderNavButtons()}</div>}
        </div>

        <div className=" flex flex-row items-center gap-3">
          {!isFullscreen && (
            <button className={`${NavBtnStyles}`} onClick={toggleFullscreen} title="Go Fullscreen" type="button">
              <Desktop className="text-primaryBlue w-5 h-5" />
            </button>
          )}
          {pdfPath && (
            <a href={pdfPath} target="_blank">
              <button className={`${NavBtnStyles}`} title="Download PDF" type="button">
                <Download className="text-primaryBlue w-5 h-5" />
              </button>
            </a>
          )}
        </div>
      </div>
    </SliderWrapper>
  ) : (
    <div className="[&_section]:mb-10">{items}</div>
  )
}
